// @file Handles Canvas format resizing
import { $ } from '@@/bits/jquery_ui_resizable'
import { getJsPlumbInstance } from '@@/bits/jsplumb'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Post } from '@@/types'
import '@@/vendor/toe.min'
import { nextTick, ref } from 'vue'

export const useResizablePost = (
  post: Post,
): {
  initializeResizable: (_containerElement: HTMLElement, mousable: boolean) => Promise<void>
  destroyResizable: () => void
} => {
  const containerElement = ref<HTMLElement>()

  const _resizeAttachment = (width: number): void => {
    const previewAspectRatio = _getAttachmentAspectRatio()
    const imageGridAspectRatio = _getImageGridAspectRatio()
    const imageGridNumOfImages = _getImageGridNumOfImages()
    const usingImageGrid = imageGridNumOfImages > 0

    // Used only when ?old=1 on surface
    const dimensions = {
      width,
      height: Math.floor(width / previewAspectRatio),
    }
    $('.beethoven-preview-image', containerElement.value).attr(dimensions).css(dimensions)
    $('.beethoven-inline-embed iframe', containerElement.value).attr(dimensions).css(dimensions)

    // Post View v2 with padding on sizes of attachment and new components
    const postV2Dimensions = {
      width: width - 16,
      height: Math.floor((width - 16) / previewAspectRatio),
    }
    $('.surface-post-attachment-inline-embed iframe', containerElement.value)
      .attr(postV2Dimensions)
      .css(postV2Dimensions)

    if (usingImageGrid) {
      // Post View v2 with image grid: we only resize the grid container as the images in the grid always
      // expand to fill the container
      // When the grid has one image, we use that image's aspect ratio
      const ar = imageGridNumOfImages === 1 ? previewAspectRatio : imageGridAspectRatio
      const imageGridDimensions = {
        width: width - 16,
        height: Math.floor((width - 16) / ar),
      }
      $('.image-grid-container', containerElement.value).attr(imageGridDimensions).css(imageGridDimensions)
    } else {
      // If not using image grid, we just resize the thumbnail image
      $('.post-attachment .image-thumbnail-crop-frame,.post-attachment .image-thumbnail-image', containerElement.value)
        .attr(postV2Dimensions)
        .css(postV2Dimensions)
    }
  }

  const _saveSize = (newSize: { width: number }): void => {
    useSurfacePostsStore().resizedPost({
      postCid: post.cid,
      size: newSize,
    })
  }

  const _getAttachmentAspectRatio = (): number => {
    return Number($('[data-aspect-ratio]', containerElement.value).data('aspect-ratio'))
  }

  const _getImageGridAspectRatio = (): number => {
    return Number($('.image-grid-container', containerElement.value).data('image-grid-aspect-ratio'))
  }

  const _getImageGridNumOfImages = (): number => {
    return Number($('.image-grid-container', containerElement.value).data('image-grid-num-of-images') || '0')
  }

  const _makeResizable = (): void => {
    if (containerElement.value == null) return

    let $realWish = $(containerElement.value) as any
    let el = $(containerElement.value)

    if ($realWish.hasClass('ui-resizable')) {
      $realWish.resizable(false)
    }

    // some users with existing canvas walls complained about new min width. so we only apply it to walls made after 2023-09-15
    // which is when we introduced the new min width
    const CANVAS_OLD_POSTS_MIN_WIDTH = 48
    const CANVAS_NEW_POSTS_MIN_WIDTH = 216
    const CANVAS_NEW_POSTS_MIN_WIDTH_TIMESTAMP = '2023-09-15'
    const isOldWall = Date.parse(useSurfaceStore().wallCreatedAt) < Date.parse(CANVAS_NEW_POSTS_MIN_WIDTH_TIMESTAMP)
    const canvasPostMinWidth = isOldWall ? CANVAS_OLD_POSTS_MIN_WIDTH : CANVAS_NEW_POSTS_MIN_WIDTH
    $realWish.resizable({
      minWidth: canvasPostMinWidth,
      handles: 'e,se',
      resize: (_event, ui) => {
        if (containerElement.value == null) return

        let elementWidth = el.width()
        let uiWidth = ui.size.width
        if (elementWidth !== uiWidth) {
          el.width(uiWidth)
        }
        _resizeAttachment(uiWidth)
        $realWish.removeClass('auto-width')
        $realWish.css('height', 'auto')
        if (ui.size.width < 100) {
          $realWish.addClass('wish-small')
        } else {
          $realWish.removeClass('wish-small')
        }
        getJsPlumbInstance()?.revalidate(containerElement.value)
      },
      start: (_event, _ui) => {
        el.addClass('in-transit')
      },
      stop: (_event, ui) => {
        let data
        el.removeClass('in-transit')
        data = {}
        data.width = ui.size.width
        _saveSize(data)
      },
      zIndex: 1003,
    })
  }

  const _makeResizableTouch = (): void => {
    if (containerElement.value == null) return

    let screenOrigin = {
      x: 0,
      y: 0,
    }

    let startWidth = 0
    let newWidth = 0

    let data = {}

    let el = $(containerElement.value)

    el.on('transformstart.resize', data, (e) => {
      const originalEvent = e.originalEvent as any
      if (originalEvent == null) return

      screenOrigin.x = (originalEvent.touches[0].clientX + originalEvent.touches[1].clientX) / 2
      screenOrigin.y = (originalEvent.touches[0].clientY + originalEvent.touches[1].clientY) / 2
      startWidth = el.width() || 0
      el.addClass('in-transit')
    })

    el.on('transform.resize', data, (e: any) => {
      newWidth = Math.max(startWidth * e.scale, 48)
      el.css('width', newWidth)
      _resizeAttachment(newWidth)
    })

    el.on('transformend.resize', data, (e) => {
      _saveSize({
        width: newWidth,
      })
      el.removeClass('in-transit')
    })
  }

  const initializeResizable = async (_containerElement: HTMLElement, mousable: boolean): Promise<void> => {
    await nextTick()
    containerElement.value = _containerElement
    mousable ? _makeResizable() : _makeResizableTouch()
  }

  const destroyResizable = (): void => {
    if (!containerElement.value) return
    const el = $(containerElement.value) as any
    const resizableInstance = el?.off('.resize').resizable('instance')
    if (resizableInstance) resizableInstance.destroy()
  }

  return {
    initializeResizable,
    destroyResizable,
  }
}
